/**
 * Animals world · "Forest & meadow": flower meadow (L1) → pond (L2) → forest edge (L3) → rocky hill (L4).
 * Far = low contrast, no outline; near = saturated, outlined (ART-BIBLE §5.3).
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { ellipse, scallop } from '../../geom';
import { bush, cloud, flower, grass, pine, ridge, rng, rock, roundTree, spread } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  hillFar: '#A9CDB4',
  hillMid: '#8DBE8A',
  meadow: '#9CCB72',
  meadowLight: '#B8DB8A',
  tree: '#6FAA62',
  treeDark: '#4E8A5C',
  trunk: '#8A5A3C',
  pine: '#3F7A5A',
  bush: '#5E9C5A',
  pond: '#7CC3CF',
  pondLight: '#B6E1E4',
  rock: '#B7AFA3',
  cloud: '#FFF9EE',
  sun: '#F6C453',
  stem: '#4E8A5C',
  grassInk: '#4E8A5C',
  flowerA: '#F27E7E',
  flowerB: '#F7D35C',
  flowerC: '#FFFFFF',
  reed: '#7A9A4E',
});

export const ANIMALS_ART: WorldArt = {
  id: 'animals',
  palette,
  theme: {
    sky: '#CFE8E4',
    skyLow: '#F3EAD0',
    ink: '#3F3226',
    label: '#FFFBF3',
    accents: ['#F27E7E', '#F7D35C', '#7CC3CF'],
    trail: { kind: 'dirt', color: '#EBD3A4', edge: '#C9A877', deco: '#D9BC8A' },
    card: { bg: '#E3F0E6', border: '#C6DEC9', shadow: '#BBD3BF' },
    node: { ring: '#E9783A', done: '#4CC38A', disc: '#FFFAF0', locked: '#E4DCCD' },
    raster: { mid: 0.66, amp: 0.07, scrim: 'rgba(255,250,240,0.14)', portrait: { mid: 0.7, amp: 0.08 } },
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(11);
    const hz = h * 0.36;
    const L: Layer[] = [];
    const z = (i: number) => zones[Math.min(i, zones.length - 1)] ?? [0, w];
    // sky props
    L.push({ k: 'region', d: ellipse(z(0)[0] + 90 * s, hz - 70 * s, 26 * s), fill: 'sun', shade: false, line: false });
    for (const x of spread(40, w, 260 * s, 3)) L.push(...cloud(x, hz - (40 + R() * 50) * s, 0.8 + R() * 0.5));
    // far hills + far pines (forest edge and rocky hill)
    L.push({ k: 'region', d: ridge(w, hz - 6 * s, 16 * s, 170 * s, 5, h + 40), fill: 'hillFar', shade: false, line: false });
    for (const x of spread(z(2)[0] - 40 * s, w, 34 * s, 7)) L.push(...pine(x, hz + 4 * s + R() * 6 * s, (0.7 + R() * 0.35) * s, 'pine', 'trunk', false));
    // mid hills + round trees
    L.push({ k: 'region', d: ridge(w, hz + 18 * s, 12 * s, 230 * s, 9, h + 40), fill: 'hillMid', shade: false, line: true });
    for (const x of spread(20, w, 70 * s, 13)) {
      const inForest = x > z(2)[0] && x < z(2)[1];
      if (!inForest && R() < 0.35) continue;
      L.push(...roundTree(x, hz + 30 * s + R() * 8 * s, (inForest ? 1 + R() * 0.3 : 0.75 + R() * 0.3) * s, R() < 0.5 ? 'tree' : 'treeDark'));
    }
    // meadow ground
    L.push({ k: 'region', d: ridge(w, hz + 40 * s, 6 * s, 260 * s, 15, h + 40), fill: 'meadow', shade: false, line: true });
    for (const x of spread(0, w, 340 * s, 17)) L.push({ k: 'region', d: scallop(x, h * 0.98, 120 * s, 22 * s, 7, 0.4, 1.1), fill: 'meadowLight', shade: false, line: false });
    // L2 pond with reeds
    const pc = (z(1)[0] + z(1)[1]) / 2;
    L.push({ k: 'region', d: ellipse(pc, h * 0.9, 120 * s, 22 * s), fill: 'pond', shade: false });
    L.push({ k: 'region', d: ellipse(pc - 20 * s, h * 0.885, 70 * s, 7 * s), fill: 'pondLight', shade: false, line: false });
    for (const dx of [-128, -118, 116, 126]) L.push({ k: 'ink', d: `M${pc + dx * s} ${h * 0.92}C${pc + dx * s} ${h * 0.86} ${pc + (dx + 2) * s} ${h * 0.82} ${pc + (dx + 4) * s} ${h * 0.79}`, w: 0.9, color: 'reed' });
    // L4 rocks
    for (const x of spread(z(3)[0] + 20 * s, w - 20, 90 * s, 19)) L.push(...rock(x, h * (0.84 + R() * 0.12), (0.8 + R() * 0.6) * s, 'rock', Math.round(x)));
    // flowers (dense in the L1 meadow) + grass
    for (const x of spread(10, w, 26 * s, 23)) {
      const dense = x < z(0)[1];
      if (!dense && R() < 0.65) continue;
      const y = h * (0.58 + R() * 0.4);
      if (Math.abs(y - h / 2) < 40 * s && R() < 0.6) continue;
      const c = R();
      L.push(...flower(x, y, c < 0.4 ? 'flowerA' : c < 0.75 ? 'flowerB' : 'flowerC', c < 0.4 ? 'flowerB' : 'flowerA', s));
    }
    for (const x of spread(0, w, 60 * s, 29)) L.push(...grass(x, h * (0.62 + R() * 0.36), s));
    // foreground bushes at the zone borders
    for (const [i, zz] of zones.entries()) if (i) L.push(...bush(zz[0], h + 6 * s, 1.5 * s));
    return { vb: [w, h], layers: L, guides: [] };
  },
};
